import type { Input } from "../../../../../api/call";
import type { Result } from "../../../../../lib/patterns/result";
import { Err, Ok, isResultErr } from "../../../../../lib/patterns/result";
import type { FILE_PARSING_ERROR } from "../../../common/file";
import { parseFile } from "../../../common/file";
import type { Parser, ParsingError, ParsingErrorMessageMap, ParsingResult } from "../../../types";
import {
  columns,
  columnsMandatory,
  hasDuplicateValue,
  hasInvalidColumnEmpty,
  hasInvalidColumns,
  hasMinSuperiorToMax,
  hasNegativeMaxStd,
  hasNotNumberValue,
  hasQuantityMaxStd,
  hasStringVeryShort,
  hasUnexpectedQuantityName,
  isValuesNotNull,
} from "./rules";
import parseCSV from "../../../../../lib/csv";
import { indexBy } from "diagnostics-lib/iterable";
/**
 * @description Data that can be parsed from a display filter file.
 */
export type DisplayFilterParsingResultData = Input<
  "POST",
  "/projects/:projectId(uuid)/displayFilters"
>["body"]["filters"];
export type DisplayFilterParsingResult = ParsingResult<DisplayFilterParsingResultData> & {};

/**
 * @description Error that can be thrown by the input model parser.
 */
export enum DISPLAY_FILTER_PARSING_ERROR {
  FILE_INVALID_COLUMNS = "FILE_INVALID_COLUMNS",
  VALUE_ALL_NULL = "VALUE_ALL_NULL",
  NAME_FILTER_IS_EMPTY = "NAME_FILTER_IS_EMPTY",
  NAME_FILTER_IS_TOO_LONG = "NAME_FILTER_IS_TOO_LONG",
  NAME_FILTER_IS_NOT_UNIQUE = "NAME_FILTER_IS_NOT_UNIQUE",
  NOT_FOUND_INVALIDATED_QUANTITY = "NOT_FOUND_INVALIDATED_QUANTITY",
  INVALID_NUMBER_MINIMUM_VALUE = "INVALID_NUMBER_MINIMUM_VALUE",
  INVALID_NUMBER_MAXIMUM_VALUE = "INVALID_NUMBER_MAXIMUM_VALUE",
  INVALID_NUMBER_MAX_STD_VALUE = "INVALID_NUMBER_MAX_STD_VALUE",
  INVALID_SOURCE_QUANTITY = "INVALID_SOURCE_QUANTITY",
  MIN_SUPERIOR_TO_MAX = "MIN_SUPERIOR_TO_MAX",
  MAX_STD_NEGATIVE = "MAX_STD_NEGATIVE",
  QUANTITY_IS_EMPTY = "QUANTITY_IS_EMPTY",
  DEFAULT_WITHOUT_GROUP = "DEFAULT_WITHOUT_GROUP",
  INVALID_DEFAULT_VALUE = "INVALID_DEFAULT_VALUE",
  GROUP_MULTIPLE_DEFAULTS = "GROUP_MULTIPLE_DEFAULTS",
}
export type DisplayFilterParsingErrorMessageMap = ParsingErrorMessageMap<
  DISPLAY_FILTER_PARSING_ERROR | FILE_PARSING_ERROR
>;
export type DisplayFilterParsingError = ParsingError<
  DISPLAY_FILTER_PARSING_ERROR | FILE_PARSING_ERROR
>;

/**
 * @description Parse input model file content.
 * @param file Input model file to parse
 * @returns
 */
export const parseDisplayFilterFile: Parser<
  DisplayFilterParsingResult,
  DisplayFilterParsingError[],
  { expectedQuantityNameValues: { id: string; name: string; formula: string | null }[] }
> = async (file, { expectedQuantityNameValues }) => {
  /**
   * @description Check for common file issue.
   */
  const errors: DisplayFilterParsingError[] = [];
  const fileResult = await parseFile(file, {
    expectedMimeType: ["text/csv", "application/vnd.ms-excel"],
  });

  /** Have to stop here, to handle file error */
  if (isResultErr(fileResult)) {
    errors.push(fileResult.value);
    return Err(errors);
  }

  const csv = await file.text();
  const data = parseCSV(csv, "line");
  const content = fileResult.value as string;
  const [header] = content.split("\n").map(line =>
    line
      .replace(/\r/, "")
      .split(";")
      .map(v => v.replaceAll('"', "")),
  );
  const columnHeaderNormalizes = columns.flatMap(([col]) => col ?? []);
  const targetData = data.map(line =>
    columnHeaderNormalizes.reduce<{ [key: string]: string | undefined }>((acc, columnName) => {
      if (columnName in acc && acc[columnName] !== undefined && acc[columnName] !== null) {
        return acc;
      }
      return {
        ...acc,
        [columnName]: line[columnName] ?? undefined,
      };
    }, {}),
  );

  /**
   * Check Format
   */
  if (hasInvalidColumns(header)) {
    errors.push({
      kind: DISPLAY_FILTER_PARSING_ERROR.FILE_INVALID_COLUMNS,
      explain: "file don't contains expected columns",
      location: { file: file.name, line: 0 },
      data: {
        hint: columnsMandatory.join(", "),
        value: header?.join(", "),
      },
    });
  }

  /**
   * @description Check by line data.
   * @see {parseData}
   */
  const dataResult = parseData(file, targetData, expectedQuantityNameValues);
  if (isResultErr(dataResult)) errors.push(...dataResult.value);

  /** if any errors, return them, otherwise return parser result */
  return errors.length > 0
    ? Err(errors)
    : Ok({
        data: dataResult.value as DisplayFilterParsingResultData,
      });
};

interface DisplayFilter {
  name: string;
  group: string | null;
  isDefault: boolean;
  quantityId: string;
  min: number | null;
  max: number | null;
  maxStd: number | null;
}

const parseData = (
  file: File,
  data: {
    [key: string]: string | undefined;
  }[],
  expectedNameValues: { id: string; name: string; formula: string | null }[],
): Result<DisplayFilterParsingResultData, DisplayFilterParsingError[]> => {
  const errors: DisplayFilterParsingError[] = [];
  const displayFilters: DisplayFilter[] = [];
  const quantityNames = expectedNameValues.map(({ name }) => name);
  const indexQuantityByName = indexBy(expectedNameValues, "name");
  const defaultSettingCountByGroup = new Map<string, number | undefined>();

  for (let line = 0; line < data.length; line++) {
    const {
      name: nameInput = "",
      default: isDefaultInput = "false",
      group: groupInput = "",
      quantity: quantityInput = "",
      minimumValue: minimumValueInput = "",
      maximumValue: maximumValueInput = "",
      maximumStd: maximumStdInput = "",
    } = data[line] || {};
    const name = nameInput.trim();
    const isDefault = isDefaultInput.trim().toLowerCase();
    const group = groupInput.trim();
    const quantity = quantityInput.trim();
    const minimumValue = minimumValueInput.trim();
    const maximumValue = maximumValueInput.trim();
    const maximumStd = maximumStdInput.trim();

    if (!["true", "false"].includes(isDefault)) {
      errors.push({
        kind: DISPLAY_FILTER_PARSING_ERROR.INVALID_DEFAULT_VALUE,
        explain: "Invalid default value",
        location: { file: file.name, line },
        data: {
          value: isDefault,
          hint: String(line + 1),
        },
      });
    }

    if (hasInvalidColumnEmpty(name)) {
      errors.push({
        kind: DISPLAY_FILTER_PARSING_ERROR.NAME_FILTER_IS_EMPTY,
        explain: "The name filter is empty",
        location: { file: file.name, line },
        data: {
          key: "name",
          hint: `${name}/${quantity}`,
          value: quantityNames.join(", "),
        },
      });
    }

    if (hasStringVeryShort(name)) {
      errors.push({
        kind: DISPLAY_FILTER_PARSING_ERROR.NAME_FILTER_IS_TOO_LONG,
        explain: "The name filter is too long",
        location: { file: file.name, line },
        data: {
          key: "name",
          hint: `${name}/${quantity}`,
        },
      });
    }

    if (hasUnexpectedQuantityName(quantity, quantityNames)) {
      errors.push({
        kind: DISPLAY_FILTER_PARSING_ERROR.NOT_FOUND_INVALIDATED_QUANTITY,
        explain: "unknown quantity name",
        location: { file: file.name, line },
        data: {
          key: "name",
          hint: `${name}/${quantity}`,
          value: quantityNames.join(", "),
        },
      });
    }

    if (hasMinSuperiorToMax(minimumValue, maximumValue)) {
      errors.push({
        kind: DISPLAY_FILTER_PARSING_ERROR.MIN_SUPERIOR_TO_MAX,
        explain: "minimumValue is superior to maximumValue",
        location: { file: file.name, line },
        data: {
          key: "minimumValue",
          hint: `${name}/${quantity}`,
          value: minimumValue,
        },
      });
    }

    if (hasNegativeMaxStd(maximumStd)) {
      errors.push({
        kind: DISPLAY_FILTER_PARSING_ERROR.MAX_STD_NEGATIVE,
        explain: "standard deviation is negative",
        location: { file: file.name, line },
        data: {
          key: name,
          hint: `${name}/${quantity}`,
          value: maximumStd,
        },
      });
    }

    if (hasQuantityMaxStd(maximumStd, quantity, indexQuantityByName)) {
      errors.push({
        kind: DISPLAY_FILTER_PARSING_ERROR.INVALID_SOURCE_QUANTITY,
        explain: "quantity does not support max standard deviation",
        location: { file: file.name, line },
        data: {
          key: "maximumStd",
          hint: `${name}/${quantity}`,
          value: maximumStd,
        },
      });
    }

    if (hasNotNumberValue(minimumValue)) {
      errors.push({
        kind: DISPLAY_FILTER_PARSING_ERROR.INVALID_NUMBER_MINIMUM_VALUE,
        explain: "values are not numbers",
        location: { file: file.name, line },
        data: {
          key: "minimumValue",
          hint: `${name}/${quantity}`,
          value: minimumValue,
        },
      });
    }
    if (hasNotNumberValue(maximumValue)) {
      errors.push({
        kind: DISPLAY_FILTER_PARSING_ERROR.INVALID_NUMBER_MAXIMUM_VALUE,
        explain: "values are not numbers",
        location: { file: file.name, line },
        data: {
          key: "maximumValue",
          hint: `${name}/${quantity}`,
          value: maximumValue,
        },
      });
    }
    if (hasNotNumberValue(maximumStd)) {
      errors.push({
        kind: DISPLAY_FILTER_PARSING_ERROR.INVALID_NUMBER_MAX_STD_VALUE,
        explain: "values are not numbers",
        location: { file: file.name, line },
        data: {
          key: "maximumStd",
          hint: `${name}/${quantity}`,
          value: maximumStd,
        },
      });
    }

    if (isValuesNotNull(maximumStd, maximumValue, minimumValue)) {
      errors.push({
        kind: DISPLAY_FILTER_PARSING_ERROR.VALUE_ALL_NULL,
        explain: "values are all null",
        location: { file: file.name, line },
        data: {
          key: "minimumValue",
          hint: `${name}/${quantity}`,
          value: `minimumValue:${minimumValue}-maximumValue:${maximumValue}-maximumStd:${maximumStd}`,
        },
      });
    }

    if (!group && isDefault === "true") {
      errors.push({
        kind: DISPLAY_FILTER_PARSING_ERROR.DEFAULT_WITHOUT_GROUP,
        explain: "Ungrouped filters cannot define a default value",
        location: { file: file.name, line },
        data: {
          value: name,
          hint: isDefault,
        },
      });
    }

    if (group && isDefault === "true") {
      defaultSettingCountByGroup.set(group, (defaultSettingCountByGroup.get(group) ?? 0) + 1);
    }

    displayFilters.push({
      name,
      isDefault: isDefault === "true",
      group: group ?? null,
      quantityId: indexQuantityByName.get(quantity)?.id ?? quantity,
      min: minimumValue ? Number(minimumValue) : null,
      max: maximumValue ? Number(maximumValue) : null,
      maxStd: maximumStd ? Number(maximumStd) : null,
    });
  }

  const duplicateNames = hasDuplicateValue(displayFilters.map(m => m.name));
  if (duplicateNames.length > 0) {
    for (const duplicateName of duplicateNames) {
      errors.push({
        kind: DISPLAY_FILTER_PARSING_ERROR.NAME_FILTER_IS_NOT_UNIQUE,
        explain: "The filter name is not unique",
        location: { file: file.name, line: 0 },
        data: {
          key: "name",
          hint: `${duplicateName}`,
        },
      });
    }
  }

  for (const [groupName, count] of defaultSettingCountByGroup.entries()) {
    if ((count ?? 0) > 1) {
      errors.push({
        kind: DISPLAY_FILTER_PARSING_ERROR.GROUP_MULTIPLE_DEFAULTS,
        explain: "A group has more than one default filter",
        location: { file: file.name, line: 0 },
        data: {
          value: groupName,
        },
      });
    }
  }

  return errors.length > 0 ? Err(errors) : Ok(displayFilters);
};
